'use client'

import { DEFAULT_APPROVAL_LIMIT } from '@/config/card'
import { hyperEVM } from '@/config/chain/constants'
import { Button } from '@/ui/atoms/button/Button'
import { Typography } from '@/ui/atoms/typography/Typography'
import { cn } from '@/ui/utils/style'
import { AlertTriangle, Globe, Shield, Wallet } from 'lucide-react'
import { type Ref } from 'react'
import { type Address } from 'viem'
import { useChainId, useSwitchChain } from 'wagmi'
import { useDelegation } from '../hooks/useDelegation'
import { useEulerDelegation } from '../hooks/useEulerDelegation'
import { useEulerWithdrawDelegation } from '../hooks/useEulerWithdrawDelegation'
import { formatApprovalError } from '../utils/formatApprovalError'
import { Info } from '@/ui/molecules/info/Info'

interface DelegationApprovalBoxProps {
  protocol?: 'pooled' | 'euler' | 'euler-withdraw'
  requiredLimit?: number
  eulerSubAccountId?: number
  borrowVaultAddress?: Address
  debtTokenAddress?: Address
  borrowTokenAddress?: Address
  supplyVaultAddress?: Address
  /**
   * Optional ref forwarded to this box's "approve / authorize" button.
   * Lets a parent CTA (e.g. the "Complete Setup" button in `CardDashboard`)
   * trigger this exact same approval path without instantiating a second
   * `useSendTransaction` hook — clicks on a disabled button are no-ops in the
   * DOM, so the box's internal `isApproving/isConfirming` gating keeps a
   * single mutation in flight across both call sites.
   */
  approveButtonRef?: Ref<HTMLButtonElement>
}

export function DelegationApprovalBox({
  protocol = 'pooled',
  requiredLimit,
  eulerSubAccountId,
  borrowVaultAddress,
  debtTokenAddress,
  borrowTokenAddress,
  supplyVaultAddress,
  approveButtonRef,
}: DelegationApprovalBoxProps) {
  if (protocol === 'euler') {
    return (
      <EulerOperatorStatus
        subAccountId={eulerSubAccountId}
        borrowVaultAddress={borrowVaultAddress}
        approveButtonRef={approveButtonRef}
      />
    )
  }

  if (protocol === 'euler-withdraw') {
    return (
      <EulerWithdrawOperatorStatus
        subAccountId={eulerSubAccountId}
        supplyVaultAddress={supplyVaultAddress}
        approveButtonRef={approveButtonRef}
      />
    )
  }

  return (
    <PooledDelegationStatus
      requiredLimit={requiredLimit}
      debtTokenAddress={debtTokenAddress}
      borrowTokenAddress={borrowTokenAddress}
      approveButtonRef={approveButtonRef}
    />
  )
}

function EulerOperatorStatus({
  subAccountId,
  borrowVaultAddress,
  approveButtonRef,
}: { subAccountId?: number; borrowVaultAddress?: Address; approveButtonRef?: Ref<HTMLButtonElement> }) {
  const chainId = useChainId()
  const { switchChain } = useSwitchChain()
  const isWrongNetwork = chainId !== hyperEVM.id

  const {
    isOperatorAuthorized,
    isControllerEnabled,
    isSetupComplete,
    isLoadingAuthorization,
    approveDelegation,
    isApproving,
    isConfirming,
    approveError,
    delegationTarget,
  } = useEulerDelegation({ subAccountId, borrowVaultAddress })

  if (isWrongNetwork) {
    return (
      <div className="flex items-center justify-between rounded-lg border border-yellow-500/30 bg-yellow-500/5 px-3 py-2.5">
        <div className="flex items-center gap-2">
          <Globe className="h-4 w-4 text-yellow-400" />
          <Typography variant="p" className="text-sm text-yellow-300">
            Switch to HyperEVM to manage authorization
          </Typography>
        </div>
        <Button
          onClick={() => switchChain({ chainId: hyperEVM.id })}
          size="sm"
          variant="outline"
          className="h-7 border-yellow-500/50 px-2.5 text-xs text-yellow-400 hover:bg-yellow-500/10"
        >
          Switch
        </Button>
      </div>
    )
  }

  if (isLoadingAuthorization) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-border/50 bg-muted/20 px-3 py-2.5">
        <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary/20 border-t-primary" />
        <Typography variant="p" className="text-muted-foreground text-sm">
          Checking position setup…
        </Typography>
      </div>
    )
  }

  if (isSetupComplete) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-border/50 bg-muted/20 px-3 py-2.5">
        <Shield className="h-4 w-4 text-green-400" />
        <Typography variant="p" className="text-muted-foreground text-sm">
          Position authorized and ready
        </Typography>
        <Info size={14}>
          <p>
            The card service is authorized to process purchases from your Mewler vault position, and the selected borrow
            vault is enabled for the position.
          </p>
          <p className="mt-1">You can revoke this authorization at any time.</p>
        </Info>
      </div>
    )
  }

  const missingController = isOperatorAuthorized && !isControllerEnabled

  return (
    <div className="space-y-2 rounded-lg border border-red-500/30 bg-red-500/5 px-3 py-2.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-red-400" />
          <Typography variant="p" className="font-medium text-red-300 text-sm">
            {missingController ? 'Position Setup Required' : 'Operator Authorization Required'}
          </Typography>
          <Info size={14} className="text-red-300/60">
            {missingController ? (
              <p>
                The operator is already authorized, but the selected borrow vault still needs to be enabled for this
                position before the card can borrow from it.
              </p>
            ) : (
              <p>
                This is a one-time approval that lets the card service process purchases from your Euler vault position.
                It does not give access to withdraw your funds.
              </p>
            )}
            <p className="mt-1">You can revoke this authorization at any time.</p>
          </Info>
        </div>
        <Button
          ref={approveButtonRef}
          onClick={() => approveDelegation()}
          disabled={isApproving || isConfirming}
          size="sm"
          className="h-7 bg-gradient-to-r from-primary to-primary/80 px-3 text-xs"
        >
          {isApproving || isConfirming ? (
            <div className="h-3 w-3 animate-spin rounded-full border-2 border-white/20 border-t-white" />
          ) : (
            'Authorize'
          )}
        </Button>
      </div>
      <Typography variant="p" className="text-red-200/60 text-xs">
        {missingController
          ? 'Finish configuring this position so the card can borrow from the selected vault.'
          : 'One-time approval to let the card operator act on your HypurrFi Prime/Yield position.'}
      </Typography>
      {approveError && (
        <Typography variant="p" className="text-red-400 text-xs">
          {formatApprovalError(approveError, 'Authorization failed.')}
        </Typography>
      )}
      {delegationTarget && (
        <Typography variant="p" className="font-mono text-[11px] text-red-300/70">
          Operator {delegationTarget.slice(0, 6)}...{delegationTarget.slice(-4)}
        </Typography>
      )}
    </div>
  )
}

function EulerWithdrawOperatorStatus({
  subAccountId,
  supplyVaultAddress,
  approveButtonRef,
}: { subAccountId?: number; supplyVaultAddress?: Address; approveButtonRef?: Ref<HTMLButtonElement> }) {
  const chainId = useChainId()
  const { switchChain } = useSwitchChain()
  const isWrongNetwork = chainId !== hyperEVM.id

  const {
    isOperatorAuthorized,
    isLoadingAuthorization,
    approveDelegation,
    isApproving,
    isConfirming,
    approveError,
    delegationTarget,
  } = useEulerWithdrawDelegation({ subAccountId, supplyVaultAddress })

  if (isWrongNetwork) {
    return (
      <div className="flex items-center justify-between rounded-lg border border-yellow-500/30 bg-yellow-500/5 px-3 py-2.5">
        <div className="flex items-center gap-2">
          <Globe className="h-4 w-4 text-yellow-400" />
          <Typography variant="p" className="text-sm text-yellow-300">
            Switch to HyperEVM to manage authorization
          </Typography>
        </div>
        <Button
          onClick={() => switchChain({ chainId: hyperEVM.id })}
          size="sm"
          variant="outline"
          className="h-7 border-yellow-500/50 px-2.5 text-xs text-yellow-400 hover:bg-yellow-500/10"
        >
          Switch
        </Button>
      </div>
    )
  }

  if (isLoadingAuthorization) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-border/50 bg-muted/20 px-3 py-2.5">
        <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary/20 border-t-primary" />
        <Typography variant="p" className="text-muted-foreground text-sm">
          Checking operator authorization…
        </Typography>
      </div>
    )
  }

  if (isOperatorAuthorized) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-border/50 bg-muted/20 px-3 py-2.5">
        <Shield className="h-4 w-4 text-green-400" />
        <Typography variant="p" className="text-muted-foreground text-sm">
          Operator authorized — spend enabled
        </Typography>
        <Info size={14}>
          <p>
            The card service is authorized to withdraw from your Euler vault position to process purchases.
          </p>
          <p className="mt-1">You can revoke this authorization at any time.</p>
        </Info>
      </div>
    )
  }

  return (
    <div className="space-y-2 rounded-lg border border-red-500/30 bg-red-500/5 px-3 py-2.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-red-400" />
          <Typography variant="p" className="font-medium text-red-300 text-sm">
            Operator Authorization Required
          </Typography>
          <Info size={14} className="text-red-300/60">
            <p>
              This is a one-time approval that lets the card service withdraw from your Euler vault position to process
              purchases.
            </p>
            <p className="mt-1">You can revoke this authorization at any time.</p>
          </Info>
        </div>
        <Button
          ref={approveButtonRef}
          onClick={() => approveDelegation()}
          disabled={isApproving || isConfirming}
          size="sm"
          className="h-7 bg-gradient-to-r from-primary to-primary/80 px-3 text-xs"
        >
          {isApproving || isConfirming ? (
            <div className="h-3 w-3 animate-spin rounded-full border-2 border-white/20 border-t-white" />
          ) : (
            'Authorize'
          )}
        </Button>
      </div>
      <Typography variant="p" className="text-red-200/60 text-xs">
        One-time approval to let the card operator spend from your Euler position.
      </Typography>
      {approveError && (
        <Typography variant="p" className="text-red-400 text-xs">
          {formatApprovalError(approveError, 'Authorization failed.')}
        </Typography>
      )}
      {delegationTarget && (
        <Typography variant="p" className="font-mono text-[11px] text-red-300/70">
          Operator {delegationTarget.slice(0, 6)}...{delegationTarget.slice(-4)}
        </Typography>
      )}
    </div>
  )
}

function PooledDelegationStatus({
  requiredLimit: propLimit,
  debtTokenAddress,
  borrowTokenAddress,
  approveButtonRef,
}: {
  requiredLimit?: number
  debtTokenAddress?: Address
  borrowTokenAddress?: Address
  approveButtonRef?: Ref<HTMLButtonElement>
}) {
  const chainId = useChainId()
  const { switchChain } = useSwitchChain()
  const isWrongNetwork = chainId !== hyperEVM.id
  const delegationLimit = propLimit ?? DEFAULT_APPROVAL_LIMIT

  const {
    currentAllowance,
    requiredLimit,
    needsDelegation,
    isLoadingAllowance,
    isAllowanceLoaded,
    approveDelegation,
    isApproving,
    isConfirming,
    approveError,
    resetError,
    delegationTarget,
  } = useDelegation({ requiredLimit: delegationLimit, debtTokenAddress, borrowTokenAddress })

  const handleApprove = async () => {
    resetError()
    try {
      await approveDelegation({ creditLimit: requiredLimit })
    } catch (error) {
      console.error('Error approving delegation:', error)
    }
  }

  if (isWrongNetwork) {
    return (
      <div className="flex items-center justify-between rounded-lg border border-yellow-500/30 bg-yellow-500/5 px-3 py-2.5">
        <div className="flex items-center gap-2">
          <Globe className="h-4 w-4 text-yellow-400" />
          <Typography variant="p" className="text-sm text-yellow-300">
            Switch to HyperEVM to manage delegation
          </Typography>
        </div>
        <Button
          onClick={() => switchChain({ chainId: hyperEVM.id })}
          size="sm"
          variant="outline"
          className="h-7 border-yellow-500/50 px-2.5 text-xs text-yellow-400 hover:bg-yellow-500/10"
        >
          Switch
        </Button>
      </div>
    )
  }

  if (!isAllowanceLoaded || isLoadingAllowance) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-border/50 bg-muted/20 px-3 py-2.5">
        <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary/20 border-t-primary" />
        <Typography variant="p" className="text-muted-foreground text-sm">
          Checking delegation…
        </Typography>
      </div>
    )
  }

  const percentRemaining = requiredLimit > 0 ? Math.min((currentAllowance / requiredLimit) * 100, 100) : 0
  const isZero = currentAllowance === 0
  const isCritical = needsDelegation
  const isCriticallyLow = !isCritical && percentRemaining < 25
  const isHealthy = !isCritical && percentRemaining >= 50

  const statusColor = isZero
    ? 'text-red-400'
    : isCritical || isCriticallyLow
      ? 'text-red-400'
      : isHealthy
        ? 'text-green-400'
        : 'text-yellow-400'
  const barColor = isZero
    ? 'bg-red-500'
    : isCritical || isCriticallyLow
      ? 'bg-red-500'
      : isHealthy
        ? 'bg-green-500'
        : 'bg-yellow-500'
  const borderColor = isZero
    ? 'border-red-500/30'
    : isCritical || isCriticallyLow
      ? 'border-red-500/30'
      : isHealthy
        ? 'border-border/50'
        : 'border-yellow-500/30'
  const bgColor = isZero
    ? 'bg-red-500/5'
    : isCritical || isCriticallyLow
      ? 'bg-red-500/5'
      : isHealthy
        ? 'bg-muted/20'
        : 'bg-yellow-500/5'

  if (isCritical) {
    const iconColor = isZero ? 'text-red-400' : 'text-amber-400'
    const textColor = isZero ? 'text-red-300' : 'text-amber-300'
    const subtleTextColor = isZero ? 'text-red-200/60' : 'text-amber-200/60'
    const bgBarColor = isZero ? 'bg-red-500' : 'bg-amber-500'
    const criticalBorderColor = isZero ? 'border-red-500/30' : 'border-amber-500/30'
    const criticalBgColor = isZero ? 'bg-red-500/5' : 'bg-amber-500/5'

    return (
      <div className={cn('space-y-3 rounded-lg border px-3 py-3', criticalBorderColor, criticalBgColor)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isZero ? (
              <AlertTriangle className="h-4 w-4 text-red-400" />
            ) : (
              <Wallet className={cn('h-4 w-4', iconColor)} />
            )}
            <Typography variant="p" className={cn('font-medium text-sm', textColor)}>
              {isZero ? 'Borrow Allowance Required' : 'Borrow Allowance Low'}
            </Typography>
            <Info size={14} className={isZero ? 'text-red-300/60' : 'text-amber-300/60'}>
              <p>
                Your HypurrFi Card spends by borrowing against your deposited collateral. The borrow allowance is the
                amount the card is permitted to spend — each purchase draws from it.
              </p>
              <p className="mt-1">
                When it runs low, you can reset it. You stay in full control and can revoke it at any time.
              </p>
            </Info>
          </div>
          <Button
            ref={approveButtonRef}
            size="sm"
            onClick={handleApprove}
            disabled={isApproving || isConfirming}
            className="h-7 bg-gradient-to-r from-primary to-primary/80 px-3 text-xs"
          >
            {isApproving || isConfirming ? (
              <div className="h-3 w-3 animate-spin rounded-full border-2 border-white/20 border-t-white" />
            ) : (
              `Set to $${requiredLimit.toLocaleString()}`
            )}
          </Button>
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs">
            <Typography variant="p" className="text-muted-foreground">
              Remaining allowance
            </Typography>
            <Typography variant="p" className={cn('font-medium', textColor)}>
              ${currentAllowance.toLocaleString(undefined, { maximumFractionDigits: 0 })} of $
              {requiredLimit.toLocaleString()}
            </Typography>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
            <div
              className={cn('h-full rounded-full transition-all duration-500', bgBarColor)}
              style={{ width: `${percentRemaining}%` }}
            />
          </div>
        </div>

        <Typography variant="p" className={cn('text-xs', subtleTextColor)}>
          {isZero
            ? 'Your card borrows against your collateral. Set an allowance so it can make purchases.'
            : `Only $${currentAllowance.toLocaleString(undefined, { maximumFractionDigits: 0 })} left — reset to $${requiredLimit.toLocaleString()} to keep spending.`}
        </Typography>
        {approveError && (
          <Typography variant="p" className="text-red-400 text-xs">
            {formatApprovalError(approveError, 'Approval failed.')}
          </Typography>
        )}
        {delegationTarget && (
          <Typography
            variant="p"
            className={cn('font-mono text-[11px]', isZero ? 'text-red-300/70' : 'text-amber-300/70')}
          >
            Delegatee {delegationTarget.slice(0, 6)}...{delegationTarget.slice(-4)}
          </Typography>
        )}
      </div>
    )
  }

  return (
    <div className={cn('space-y-2 rounded-lg border px-3 py-2.5', borderColor, bgColor)}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Wallet className={cn('h-4 w-4', statusColor)} />
          <Typography variant="p" className="font-medium text-sm">
            Borrow Delegation
          </Typography>
          <Info size={14}>
            <p>
              Your HypurrFi Card spends by borrowing against your deposited collateral. Each purchase draws from your
              approved allowance.
            </p>
            <p className="mt-1">
              When it runs low, you can reset it. You stay in full control and can revoke it at any time.
            </p>
          </Info>
        </div>
        <div className="flex items-center gap-2">
          <Typography variant="p" className={cn('font-semibold text-sm', statusColor)}>
            ${currentAllowance.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </Typography>
          <Typography variant="p" className="text-muted-foreground text-xs">
            of ${requiredLimit.toLocaleString()}
          </Typography>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="flex-1">
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div
              className={cn('h-full rounded-full transition-all duration-500', barColor)}
              style={{ width: `${Math.min(100, percentRemaining)}%` }}
            />
          </div>
        </div>
        <Typography variant="p" className="w-8 text-right text-muted-foreground text-xs">
          {percentRemaining.toFixed(0)}%
        </Typography>
        {(isCriticallyLow || Math.abs(currentAllowance - requiredLimit) > 0.5) && (
          <Button
            size="sm"
            variant="secondary"
            onClick={handleApprove}
            disabled={isApproving || isConfirming}
            className={cn(
              'h-7 px-2.5 text-xs',
              isCriticallyLow
                ? 'bg-gradient-to-r from-primary to-primary/80 text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground',
            )}
          >
            {isApproving || isConfirming ? (
              <div className="h-3 w-3 animate-spin rounded-full border-2 border-white/20 border-t-white" />
            ) : (
              'Reset'
            )}
          </Button>
        )}
      </div>

      {isCriticallyLow && (
        <Typography variant="p" className="text-red-300/80 text-xs">
          Running low — reset allowance to keep spending.
        </Typography>
      )}
      {approveError && (
        <Typography variant="p" className="text-red-400 text-xs">
          {formatApprovalError(approveError, 'Failed.')}
        </Typography>
      )}
      {delegationTarget && (
        <Typography variant="p" className="font-mono text-[11px] text-muted-foreground/80">
          Delegatee {delegationTarget.slice(0, 6)}...{delegationTarget.slice(-4)}
        </Typography>
      )}
    </div>
  )
}
